import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { ConsultationRequestAddressComponent } from '@components/consultation-request/consultation-request-address/consultation-request-address.component';
import { ConsultationRequestFormComponent } from '@components/consultation-request/consultation-request-form/consultation-request-form.component';
import { ConsultationRequestPatientInfoComponent } from '@components/consultation-request/consultation-request-patient-info/consultation-request-patient-info.component';
import { LabFinderModalComponent } from '@components/consultation-request/lab-finder-modal/lab-finder-modal.component';
import { MedicalHistoryQuestionsComponent } from '@components/consultation-request/medical-history-questions/medical-history-questions.component';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { DynamicQuestionnaireFormComponent } from '@components/dynamic-forms/dynamic-questionnaire-form/dynamic-questionnaire-form.component';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';
import { TestCenterComponent } from '@components/test-center/test-center.component';
import { FreeTestosteronePanelTest } from '@data/tests';
import { ConsultationStorageKeys } from '@enums/consultation-storage-keys';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { StorageKeys } from '@enums/storage-keys';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { LabRequiredError } from '@errors/lab-required-error';
import { ResultService } from '@Medology/ng-findalab';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-trt-consultation-request-form',
  templateUrl: 'trt-consultation-request-form.component.html',
  styleUrls: ['trt-consultation-request-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ErrorNotificationComponent,
    ConsultationRequestHeaderComponent,
    ConsultationRequestPatientInfoComponent,
    CardComponent,
    DynamicQuestionnaireFormComponent,
    MedicalHistoryQuestionsComponent,
    ConsultationRequestAddressComponent,
    LabFinderModalComponent,
    TestCenterComponent,
  ],
})
export class TrtConsultationRequestFormComponent extends ConsultationRequestFormComponent implements OnInit, OnDestroy {
  private static readonly HAS_MANDATORY_LAB_QUESTION_ID = '274';

  @ViewChild(TestCenterComponent, { read: ElementRef })
  private testCenterElementRef: ElementRef;

  public showChooseALab: boolean = false;

  private resultService = inject(ResultService);
  private labSelectionSubscription: Subscription;
  private hasMandatoryLabAnswerSubscription: Subscription;

  /**
   * Determines whether in-home collection is allowed based on the application configuration.
   */
  public get inHomeCollectionAllowed(): boolean {
    return environment.ngFindalab.inHomeCollectionAllowed;
  }

  /**
   * Determines whether the test center selection is required based on the presence of a mandatory lab result.
   */
  private get requiresTestCenter(): boolean {
    return this.consultationStorageService.hasMandatoryLabResult === null;
  }

  /**
   * Gets the form control for the lab selection.
   */
  private get labControl() {
    return this.formService.checkout.get('lab_id');
  }

  /**
   * Handles the event when the medical questionnaire form is ready.
   *
   * @param {FormGroup} form The medical questionnaire form group.
   */
  public onMedicalQuestionnaireFormReady(form: FormGroup): void {
    const control = form.get(TrtConsultationRequestFormComponent.HAS_MANDATORY_LAB_QUESTION_ID);
    if (!control) {
      return;
    }

    this.hasMandatoryLabAnswerSubscription = control.valueChanges
      .pipe(startWith(control.value))
      .subscribe((value) => (this.showChooseALab = value === QuestionnaireAnswers.No));
  }

  public ngOnInit() {
    super.ngOnInit();
    this.storageService.tests = [FreeTestosteronePanelTest];
    this.storageService.providerIds = environment.ngFindalab.inHomeCollectionProviderIds;
    this.labSelectionSubscription = this.resultService.resultSelected$.subscribe(() => {
      this.testCenterElementRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  public ngOnDestroy(): void {
    this.hasMandatoryLabAnswerSubscription?.unsubscribe();
    this.labSelectionSubscription?.unsubscribe();
  }

  /**
   * {@inheritDoc}
   */
  public override continue(): void {
    super.continue();

    if (this.submissionError && this.requiresTestCenter && this.labControl.invalid) {
      this.submissionError = new LabRequiredError().message;
    }
  }

  /**
   * {@inheritDoc}
   */
  protected override handlePharmacyForm(): void {
    this.pharmacyForm.patchValue({
      id: this.treatmentTypeService.getTreatmentType(this.consultationRequestTreatmentType).designatedPharmacyId,
    });
  }

  /**
   * {@inheritDoc}
   */
  protected override areFormsValid(): boolean {
    return super.areFormsValid() && (!this.requiresTestCenter || this.labControl.valid);
  }

  /**
   * {@inheritDoc}
   */
  protected override markAllFormControlsAsTouched(): void {
    super.markAllFormControlsAsTouched();

    if (this.requiresTestCenter) {
      this.labControl.markAsTouched();
    }
  }

  /**
   * {@inheritDoc}
   */
  protected override scrollToInvalidControl(): void {
    if (this.requiresTestCenter && this.labControl.invalid) {
      this.testCenterElementRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    super.scrollToInvalidControl();
  }

  /**
   * {@inheritDoc}
   */
  protected override storeConsultationRequestCompletedData() {
    super.storeConsultationRequestCompletedData();

    this.handleMandatoryLabResult();
  }

  /**
   * Handle mandatory lab result question.
   */
  private handleMandatoryLabResult(): void {
    this.consultationStorageService.remove(ConsultationStorageKeys.Lab);

    if (this.consultationStorageService.hasMandatoryLabResult === QuestionnaireAnswers.Yes) {
      this.removeTestCentersFromStorage();
      // Upsells don't need to be removed because super.storeConsultationRequestCompletedData() already handles it

      return;
    }

    this.setLabFromTestCenter();
    this.addFreeTestosteronePanelUpsell();
  }

  /**
   * Add Free Testosterone Panel upsell to the consultation storage service.
   */
  private addFreeTestosteronePanelUpsell() {
    this.consultationStorageService.upsells = [
      ...this.consultationStorageService.upsells,
      UpsellSlugs.FreeTestosteronePanel,
    ];
  }

  /**
   * Set the lab in the consultation storage service from the selected test center in the storage service.
   */
  private setLabFromTestCenter() {
    if (this.storageService.center) {
      this.consultationStorageService.lab = this.storageService.center;
    }
  }

  /**
   * Remove test centers from the storage service and consultation storage service.
   */
  private removeTestCentersFromStorage() {
    this.storageService.remove(StorageKeys.Center);
  }
}
